/**
 * The Variables row of a repository's sheet: one list of every name its
 * builds read, wherever the value is set. The index's detected names come
 * first, then the names set on this repository by hand, then the workspace's.
 *
 * Each row says where its value comes from: this repository, the workspace,
 * or nowhere yet. Where to set a value is chosen at the moment of setting it.
 * A value for this repository is staged and saved with the next run; a value
 * for every repository is a workspace variable, saved at once.
 */
import { useState } from "react";
import { Button, Chip, Input } from "../ui/index.js";
import { EnvPaste } from "./EnvironmentConfigRows.js";
import { Row } from "./environmentControls.js";
import { GithubPackagesTokenHelp, isGithubPackagesToken } from "./GithubPackagesTokenHelp.js";
import {
  VARIABLE_CHIP_LABELS,
  isVariableName,
  type RepositoryVariableRow,
  type VariableChip,
} from "./environmentPage.js";
import { setWhen } from "./WorkspaceVariables.js";
import { WORKSPACE_VARIABLES_FAILED, setWorkspaceVariables } from "./workspaceVariablesApi.js";

const CHIP_TONES: Record<VariableChip, "success" | "neutral" | "dashed"> = {
  set_here: "success",
  from_workspace: "neutral",
  missing: "dashed",
};

type Scope = "repository" | "workspace";

function VariableRow({
  row,
  focus,
  busy,
  onStage,
  onToggle,
  onSetWorkspace,
}: {
  row: RepositoryVariableRow;
  /** The row What runs here asked for: the cursor goes to its value. */
  focus: boolean;
  busy: boolean;
  onStage: (values: Record<string, string>) => void;
  onToggle: (name: string) => void;
  onSetWorkspace: (values: Record<string, string>) => Promise<boolean>;
}) {
  const [value, setValue] = useState("");
  const { name, chip } = row;
  const amber = row.required && chip === "missing";
  const stage = () => {
    onStage({ [name]: value });
    setValue("");
  };
  return (
    <div className="wrk-srow" data-testid={`qa-variable-${name}`}>
      <span className="wrk-srow-text">
        <span className="wrk-srow-title">
          {name}{" "}
          <Chip
            tone={amber ? "honesty" : CHIP_TONES[chip]}
            data-testid={`qa-variable-chip-${name}`}
            data-required={amber ? "true" : undefined}
          >
            {VARIABLE_CHIP_LABELS[chip]}
          </Chip>
        </span>
        {row.meaning && <span className="wrk-srow-help">{row.meaning}</span>}
        {isGithubPackagesToken(name) && (
          <span className="wrk-srow-help">
            <GithubPackagesTokenHelp />
          </span>
        )}
        {row.removed ? (
          <span className="wrk-srow-help">Removed on save</span>
        ) : row.reenter ? (
          <span className="wrk-srow-help">Value cleared. Enter it again below.</span>
        ) : row.staged ? (
          <span className="wrk-srow-help">New value, saved with the next run</span>
        ) : null}
        {chip === "set_here" && row.workspaceSetAt && (
          <span className="wrk-srow-help">Overrides the workspace value</span>
        )}
        {chip === "from_workspace" && row.workspaceSetAt && (
          <span className="wrk-srow-help">{setWhen(row.workspaceSetAt)} for every repository</span>
        )}
      </span>
      <span className="wrk-srow-controls">
        {row.saved && (
          <Button variant="ghost" onClick={() => onToggle(name)}>
            {row.removed ? `Keep ${name}` : `Remove ${name}`}
          </Button>
        )}
        <Input
          type="password"
          aria-label={`Value for ${name}`}
          value={value}
          autoComplete="new-password"
          data-autofocus={focus ? "true" : undefined}
          onChange={(event) => setValue(event.target.value)}
        />
        {chip === "missing" ? (
          <>
            <Button variant="secondary" disabled={!value || busy} onClick={stage}>
              Set for this repository
            </Button>
            <Button
              variant="ghost"
              disabled={!value || busy}
              onClick={() =>
                void onSetWorkspace({ [name]: value }).then((ok) => ok && setValue(""))
              }
            >
              Set for every repository
            </Button>
          </>
        ) : (
          <Button variant="secondary" disabled={!value || busy} onClick={stage}>
            {chip === "set_here" ? "Replace" : "Override here"}
          </Button>
        )}
      </span>
    </div>
  );
}

export function RepositoryVariables({
  rows,
  workspaceError,
  reloadWorkspace,
  ask,
  name,
  value,
  setName,
  setValue,
  onStage,
  onToggle,
}: {
  /** From `repositoryVariableRows`: the same list the row's summary counts. */
  rows: readonly RepositoryVariableRow[];
  /** The workspace's variables could not be read: their rows may be missing. */
  workspaceError: string | null;
  reloadWorkspace: () => Promise<void>;
  /** A name another part of the sheet asked to set (What runs here's Set). */
  ask: string | null;
  /** The half-typed pair in "Add a variable"; a run waits while it is filled in. */
  name: string;
  value: string;
  setName: (name: string) => void;
  setValue: (value: string) => void;
  onStage: (values: Record<string, string>) => void;
  onToggle: (name: string) => void;
}) {
  const [scope, setScope] = useState<Scope>("repository");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolves true once the save landed, whatever the read after it does: a
  // failed read says so through `workspaceError`, never as a failed save.
  async function setForWorkspace(values: Record<string, string>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await setWorkspaceVariables(values);
    } catch (err) {
      setError(err instanceof Error ? err.message : WORKSPACE_VARIABLES_FAILED);
      setBusy(false);
      return false;
    }
    await reloadWorkspace().catch(() => undefined);
    setBusy(false);
    return true;
  }

  const ready = isVariableName(name) && value.length > 0;
  const add = () => {
    if (scope === "repository") {
      onStage({ [name]: value });
      setName("");
      setValue("");
      return;
    }
    void setForWorkspace({ [name]: value }).then((ok) => {
      if (!ok) return;
      setName("");
      setValue("");
    });
  };

  return (
    <div className="qa-variables-table" data-testid="qa-variables">
      {rows.length > 0 && (
        <Row
          title="Where each value comes from"
          help="This repository's own value wins over the workspace's. Values are write-only."
        />
      )}
      {(error ?? workspaceError) && (
        <p role="alert" className="qa-notice">
          {error ?? workspaceError}
        </p>
      )}
      {rows.map((row) => (
        <VariableRow
          key={row.name}
          row={row}
          focus={row.name === ask}
          busy={busy}
          onStage={onStage}
          onToggle={onToggle}
          onSetWorkspace={setForWorkspace}
        />
      ))}
      <Row
        title="Add a variable"
        help="Only the name is ever shown again."
        testId="qa-add-variable"
      >
        {/* Name, value, scope and Add stack in a column: side by side they
            overflow the sheet's narrow column. */}
        <span className="wrk-srow-controls qa-service qa-add-variable">
          <label>
            Variable name
            <Input
              value={name}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              onChange={(event) => setName(event.target.value.trim())}
            />
          </label>
          <label>
            Variable value
            <Input
              type="password"
              value={value}
              autoComplete="new-password"
              disabled={busy}
              onChange={(event) => setValue(event.target.value)}
            />
          </label>
          <fieldset className="qa-scope">
            <legend>Set for</legend>
            <label>
              <input
                type="radio"
                name="variable-scope"
                checked={scope === "repository"}
                onChange={() => setScope("repository")}
              />
              This repository, saved with the next run
            </label>
            <label>
              <input
                type="radio"
                name="variable-scope"
                checked={scope === "workspace"}
                onChange={() => setScope("workspace")}
              />
              Every repository in the workspace, saved now
            </label>
          </fieldset>
          <Button variant="secondary" disabled={busy || !ready} onClick={add}>
            {scope === "repository" ? "Set for this repository" : "Set for every repository"}
          </Button>
        </span>
      </Row>
      <EnvPaste
        title="Paste a .env for this repository"
        help="Each NAME=value line becomes a value for this repository, saved with the next run."
        actionLabel="Stage variables"
        onStage={onStage}
      />
    </div>
  );
}
